import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import PrintDialog from '@/components/shared/PrintDialog';
import ProductionScheduleReportPrintContent from './ProductionScheduleReportPrintContent';
import { useCompanyConfig } from '@/hooks/useCompanyConfig';
import { Loader2, Download, Filter, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

interface ProductionScheduleReportPrintModalProps {
  open: boolean;
  onClose: () => void;
}

type DatePreset = 'today' | 'tomorrow' | 'thisWeek' | 'thisMonth' | 'custom';

function getLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function ProductionScheduleReportPrintModal({
  open,
  onClose,
}: ProductionScheduleReportPrintModalProps) {
  const companyConfig = useCompanyConfig(open);

  // Date calculation
  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => getLocalDateString(today), [today]);

  const tomorrowStr = useMemo(() => {
    const tm = new Date();
    tm.setDate(tm.getDate() + 1);
    return getLocalDateString(tm);
  }, []);

  const firstDayOfWeekStr = useMemo(() => {
    const curr = new Date();
    const day = curr.getDay() || 7; // 1 (Mon) - 7 (Sun)
    curr.setDate(curr.getDate() - day + 1);
    return getLocalDateString(curr);
  }, []);

  const firstDayOfMonthStr = useMemo(() => {
    const f = new Date();
    f.setDate(1);
    return getLocalDateString(f);
  }, []);

  // Filter States
  const [preset, setPreset] = useState<DatePreset>('today');
  const [dateFrom, setDateFrom] = useState<string>(todayStr);
  const [dateTo, setDateTo] = useState<string>(todayStr);
  const [selectedProcesses, setSelectedProcesses] = useState<string[]>([]);
  const [showProcessDropdown, setShowProcessDropdown] = useState(false);

  const handleSelectPreset = (p: DatePreset) => {
    setPreset(p);
    if (p === 'today') {
      setDateFrom(todayStr);
      setDateTo(todayStr);
    } else if (p === 'tomorrow') {
      setDateFrom(tomorrowStr);
      setDateTo(tomorrowStr);
    } else if (p === 'thisWeek') {
      setDateFrom(firstDayOfWeekStr);
      setDateTo(todayStr);
    } else if (p === 'thisMonth') {
      setDateFrom(firstDayOfMonthStr);
      setDateTo(todayStr);
    }
  };

  // Fetch report data
  const { data: reportData, isLoading, isFetching } = useQuery({
    queryKey: ['production-schedule-report', dateFrom, dateTo, selectedProcesses],
    queryFn: async () => {
      return (trpcClient as any).productionPlanning.getProductionScheduleReport.query({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        processNames: selectedProcesses.length > 0 ? selectedProcesses : undefined,
      });
    },
    enabled: open,
    staleTime: 1000 * 60,
  });

  const availableProcesses: string[] = reportData?.availableProcesses || [
    'In Offset', 'Cán màng', 'Bế decal/hộp', 'Ép kim/UV', 'Dán hộp', 'Đóng gói'
  ];

  const toggleProcess = (proc: string) => {
    setSelectedProcesses(prev =>
      prev.includes(proc) ? prev.filter(p => p !== proc) : [...prev, proc]
    );
  };

  // Handle Excel Export matching khsx_report.html
  const handleExportExcel = () => {
    if (!reportData?.items || reportData.items.length === 0) {
      toast.error('Không có dữ liệu kế hoạch sản xuất để xuất Excel');
      return;
    }

    try {
      const rows = reportData.items.map((item: any, idx: number) => ({
        'STT': idx + 1,
        'Công Đoạn': item.processGroup || item.stepName || '',
        'Mã LSX': item.poCode || '',
        'Khách Hàng': item.customerName || '',
        'Mã SP': item.productCode || '',
        'Tên Sản Phẩm': item.productName || item.tenSp || '',
        'SL Cấp': item.slCap || 0,
        'SL Bù': item.slBu || 0,
        'Dự Kiến': item.slDuKien || 0,
        'SL Đạt': item.slDat || 0,
        'SL Hư': item.slHu || 0,
        'Hao Hụt': item.haoHut || 0,
        'Trạng Thái': item.status || '',
        'Người Thực Hiện / Máy': item.nguoiThucHien || '',
      }));

      // Add Grand Total
      rows.push({
        'STT': 'TỔNG CỘNG',
        'Công Đoạn': '',
        'Mã LSX': '',
        'Khách Hàng': '',
        'Mã SP': '',
        'Tên Sản Phẩm': '',
        'SL Cấp': reportData.summary?.totalCap || 0,
        'SL Bù': reportData.summary?.totalBu || 0,
        'Dự Kiến': reportData.summary?.totalDuKien || 0,
        'SL Đạt': reportData.summary?.totalDat || 0,
        'SL Hư': reportData.summary?.totalHu || 0,
        'Hao Hụt': reportData.summary?.totalHao || 0,
        'Trạng Thái': '',
        'Người Thực Hiện / Máy': '',
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'KHSX A4 Ngang');
      XLSX.writeFile(
        workbook,
        `Bao_Cao_KHSX_${dateFrom}_${dateTo}_${Date.now()}.xlsx`
      );
      toast.success('Đã xuất file Excel KHSX thành công!');
    } catch (err: any) {
      toast.error('Lỗi khi xuất file Excel: ' + (err.message || ''));
    }
  };

  if (!open) return null;

  // Custom Toolbar Controls on top of PrintDialog
  const extraControls = (
    <div className="hidden lg:flex items-center gap-2 text-xs">
      {/* Preset pills */}
      <div className="flex items-center bg-slate-100 p-0.5 rounded-md border border-slate-200">
        <button
          type="button"
          onClick={() => handleSelectPreset('today')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'today' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Hôm nay
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('tomorrow')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'tomorrow' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Ngày mai
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('thisWeek')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'thisWeek' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tuần này
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('thisMonth')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'thisMonth' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tháng này
        </button>
        <button
          type="button"
          onClick={() => setPreset('custom')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'custom' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tùy chọn
        </button>
      </div>

      {/* Date Pickers */}
      <div className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 text-[11px]">
        <span className="text-slate-500">Từ:</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPreset('custom');
          }}
          className="bg-transparent border-0 p-0 text-slate-800 text-[11px] focus:outline-hidden cursor-pointer"
        />
        <span className="text-slate-500">Đến:</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPreset('custom');
          }}
          className="bg-transparent border-0 p-0 text-slate-800 text-[11px] focus:outline-hidden cursor-pointer"
        />
      </div>

      {/* Multi-process Filter Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowProcessDropdown(!showProcessDropdown)}
          className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded text-slate-700 font-medium hover:bg-slate-100"
        >
          <Filter className="w-3 h-3 text-indigo-600" />
          <span>
            {selectedProcesses.length === 0
              ? 'Tất cả công đoạn'
              : `${selectedProcesses.length} công đoạn`}
          </span>
        </button>

        {showProcessDropdown && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl p-2 z-50 max-h-60 overflow-y-auto">
            <div className="text-[11px] font-bold text-slate-500 px-1 mb-1 flex justify-between">
              <span>CHỌN CÔNG ĐOẠN</span>
              {selectedProcesses.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedProcesses([])}
                  className="text-indigo-600 hover:underline cursor-pointer"
                >
                  Xóa chọn
                </button>
              )}
            </div>
            {availableProcesses.map((proc) => {
              const checked = selectedProcesses.includes(proc);
              return (
                <label
                  key={proc}
                  className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-slate-800"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleProcess(proc)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                  />
                  <span className="break-words whitespace-normal">{proc}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Excel Export Button */}
      <button
        type="button"
        onClick={handleExportExcel}
        className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 px-2.5 py-1.5 rounded font-medium transition-colors cursor-pointer"
        title="Xuất bảng KHSX ra file Excel"
      >
        <Download className="h-3.5 w-3.5 text-emerald-600" />
        <span>Xuất Excel</span>
      </button>
    </div>
  );

  return (
    <PrintDialog
      open={open}
      onClose={onClose}
      type="custom"
      orientation="landscape"
      documentTitle={`Bao_Cao_KHSX_${dateFrom}_${dateTo}`}
      extraControls={extraControls}
    >
      {isLoading || isFetching ? (
        <div className="py-24 flex flex-col items-center justify-center text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
          <p className="font-medium text-sm">Đang tải kế hoạch sản xuất theo công đoạn...</p>
        </div>
      ) : (
        <ProductionScheduleReportPrintContent
          reportData={reportData || { items: [], summary: { totalCap: 0, totalBu: 0, totalDuKien: 0, totalDat: 0, totalHu: 0, totalHao: 0, totalItems: 0 } }}
          dateRange={{ from: dateFrom, to: dateTo }}
          selectedProcesses={selectedProcesses}
          config={companyConfig}
        />
      )}
    </PrintDialog>
  );
}

export default ProductionScheduleReportPrintModal;
